// 書籍詳細画面
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, fmtDate, renderLongText, backToHome } from '../ui.js';
import { fetchPageNote, fetchCover, requestIllustration, fetchNextQuestion, appendNotionNote, fetchPreReadingReflection } from '../api.js';
import { getBook, upsertBook, saveAiHistory, getAiHistoryByType, deleteBook, addHighlight, getHighlightsByBook, deleteHighlight, getAllAiHistory } from '../cache.js';
import { showAnalyzeScreen } from './analyze.js';
import { showQuoteCardScreen } from './quote-card.js';
import { showContrastScreen } from './contrast.js';

let _currentBookId = null;

// Notion ページ本文の「## メモソース」セクションから Google Drive URL を抽出する。
// /reading-log-sync スキルが書き込んだ「- 元 PDF: [Google Drive で開く](Drive viewUrl)」を拾う。
function extractBooxPdfUrl(note) {
  if (!note) return '';
  const m = note.match(/\[Google Drive で開く\]\((https?:\/\/[^)\s]+)\)/);
  if (m) return m[1];
  // フォールバック: メモソースセクション内の素の Drive URL
  const m2 = note.match(/##\s*メモソース[\s\S]*?(https?:\/\/(?:drive|docs)\.google\.com\/[^\s)]+)/);
  return m2 ? m2[1] : '';
}

export async function showBookDetailScreen(bookId) {
  _currentBookId = bookId;
  let book = await getBook(bookId);
  if (!book) {
    toast('書籍が見つかりません');
    return;
  }
  renderBookDetail(book);

  // localOnly 書籍は Notion ページではないので fetchPageNote をスキップ
  if (!book.localOnly && !bookId.startsWith('local-')) {
    try {
      const { note } = await fetchPageNote(bookId);
      if (note && note !== book.note) {
        book = { ...book, note };
        await upsertBook(book);
        renderBookDetail(book);
      }
    } catch (e) {
      console.error('Failed to fetch note:', e);
    }
  }

  // openBD で書影が取れる本もまだあるので、書影 / illustration いずれも無い場合のみ取得を試行
  if (!book.coverUrl && !book.hasIllustration && !book.illustrationDataUrl && !book.illustrationBlob && book.name) {
    try {
      const { coverUrl } = await fetchCover(book.name, book.author);
      if (coverUrl) {
        book = { ...book, coverUrl };
        await upsertBook(book);
        renderBookDetail(book);
      }
    } catch (e) {
      console.error('Cover fetch failed:', e);
    }
  }
}

function coverSrcFor(book) {
  if (book.hasIllustration) return `/illustrations/${book.id}.png`;
  if (book.illustrationDataUrl) return book.illustrationDataUrl;
  if (book.illustrationBlob) return URL.createObjectURL(book.illustrationBlob);
  if (book.coverUrl) return book.coverUrl;
  return null;
}

// Blob → data URL 文字列に変換する。
// iOS Safari / standalone PWA(WebKit) には「IndexedDB に保存した Blob が
// アプリ再起動後に読み出せなくなる」既知バグがあり、生成直後は表示できても
// 強制終了すると書影が消える。data URL 文字列なら構造化クローンで確実に永続化される。
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

// 向き合い度（★1-5）を儀式の達成数から算出する。
// 「私（読者）とこの本との関係の深さ」を数値化（本そのものの評価とは別）。
async function computeEngagement(book) {
  const histories = await getAllAiHistory();
  const forThis = histories.filter((h) => h.context?.bookId === book.id);
  const hasShakyo = histories.some((h) => h.type === 'shakyo' && h.context?.bookId === book.id);
  const hasChat = forThis.some((h) => h.type === 'book-chat');
  const hasNextQ = forThis.some((h) => h.type === 'next-question');
  const hasAnalyze = forThis.some((h) => h.type === 'analyze');
  const hasMyBib = forThis.some((h) => h.type === 'my-bib');
  const noteLen = (book.note || '').trim().length;

  // 段階別の達成判定
  const ladder = [
    noteLen > 0 || book.status === '積読' || book.status === '読書中' || book.status === '読了',  // ★1: 出会った
    noteLen > 80 || book.status === '読書中' || book.status === '読了',  // ★2: 読み始めた
    book.status === '読了' || noteLen > 400,  // ★3: 読了
    hasNextQ || hasAnalyze || hasChat || hasMyBib,  // ★4: 深く向き合った
    hasShakyo && (hasChat || hasNextQ || hasMyBib),  // ★5: 写経までして思考の地層になった
  ];
  let score = 0;
  for (const ok of ladder) {
    if (ok) score += 1;
    else break;
  }
  return { score, signals: { hasShakyo, hasChat, hasNextQ, hasAnalyze, hasMyBib, noteLen, status: book.status } };
}

function renderBookDetail(book) {
  const amazonUrl = `https://www.amazon.co.jp/s?k=${encodeURIComponent(book.name + ' ' + (book.author || ''))}`;
  const koshoUrl = `https://www.kosho.or.jp/products/list.php?search_word=${encodeURIComponent(book.name + ' ' + (book.author || ''))}`;
  const notionUrl = book.url || '';

  const coverSrc = coverSrcFor(book);
  const coverHtml = coverSrc
    ? `<img class="book-detail__cover" src="${escapeHtml(coverSrc)}" alt="" referrerpolicy="no-referrer" />`
    : `
        <div class="book-detail__cover-fallback">${escapeHtml(book.name?.[0] || '?')}</div>
        <button class="btn btn--block btn--small" data-action="open-illustrate" type="button" style="max-width:240px;margin:0 auto var(--space-5);">
          <svg class="icon"><use href="#i-sparkles"/></svg>
          <span>AI でサムネイルを生成</span>
        </button>
      `;

  const genreChips = (book.genre || [])
    .map((g) => `<span class="chip">${escapeHtml(g)}</span>`)
    .join('');

  const noteHtml = book.note
    ? `<div class="book-detail__note long-text">${renderLongText(book.note)}</div>
       <div class="next-question-box" id="next-question-box">
         <button class="btn btn--block btn--ghost" id="btn-next-question" type="button">
           <svg class="icon"><use href="#i-sparkles"/></svg>
           <span>AI に「次の問い」を提案させる</span>
         </button>
         <div id="next-question-result" hidden></div>
       </div>
       <div class="note-append" id="note-append">
         <p class="note-append__label">メモを追記</p>
         <textarea id="note-append-text" class="form-textarea" rows="3" placeholder="読書中に思いついた一言を書く..."></textarea>
         <button class="btn btn--small btn--block btn--ink" id="btn-append-note" type="button" style="margin-top:8px;">
           <svg class="icon"><use href="#i-plus"/></svg>
           <span>${book.localOnly ? 'ローカルに追記' : 'Notion ページ末尾に追記'}</span>
         </button>
       </div>`
    : `<div class="book-detail__note-empty">メモはまだありません</div>
       <div class="note-append" id="note-append">
         <p class="note-append__label">最初のメモを書く</p>
         <textarea id="note-append-text" class="form-textarea" rows="3" placeholder="この本との出会いを一言..."></textarea>
         <button class="btn btn--small btn--block btn--ink" id="btn-append-note" type="button" style="margin-top:8px;">
           <svg class="icon"><use href="#i-plus"/></svg>
           <span>${book.localOnly ? 'ローカルに保存' : 'Notion ページ末尾に追記'}</span>
         </button>
       </div>`;

  const engagementStars = '<span id="engagement-stars">…</span>';
  const acqHtml = book.acquisition
    ? `<div class="acq-box"><p class="acq-box__label">この本との出会い</p><p class="acq-box__body">${escapeHtml(book.acquisition)}</p></div>`
    : '';
  const preQHtml = book.preReadingQuestion
    ? `<div class="pre-q-box"><p class="pre-q-box__label">読書前の問い</p><p class="pre-q-box__body">${escapeHtml(book.preReadingQuestion)}</p></div>`
    : '';
  const closingHtml = book.closingNote
    ? `<div class="closing-box"><p class="closing-box__label">本を閉じる時の一言</p><p class="closing-box__body">${escapeHtml(book.closingNote)}</p></div>`
    : '';

  const html = `
    ${screenHead('書籍詳細')}
    ${coverHtml}
    <h2 class="book-detail__title">${escapeHtml(book.name)}</h2>
    <p class="book-detail__author">${escapeHtml(book.author || '著者未登録')}</p>
    <div class="book-detail__meta">
      <span>${escapeHtml(book.status || '')}</span>
      ${book.rating ? `<span>${escapeHtml(book.rating)}</span>` : ''}
      ${book.startDate ? `<span>${fmtDate(book.startDate)}${book.endDate ? '〜' + fmtDate(book.endDate) : ''}</span>` : ''}
    </div>
    <div class="engagement" data-engagement style="text-align:center;margin-bottom:14px;">
      <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">engagement</p>
      ${engagementStars}
    </div>
    ${genreChips ? `<div class="result-card__chips" style="justify-content:center;margin-bottom:20px;">${genreChips}</div>` : ''}
    ${acqHtml}
    ${preQHtml}
    ${noteHtml}
    ${closingHtml}
    <div id="highlights-box" class="highlights-box"></div>
    <div class="book-detail__actions">
      <button class="btn btn--ink btn--block" data-action="book-chat" type="button">
        <svg class="icon"><use href="#i-chat"/></svg>
        <span>この本に話しかける</span>
      </button>
      <button class="btn btn--block" data-action="analyze" type="button">
        <svg class="icon"><use href="#i-eye"/></svg>
        <span>哲学者の視座で分析</span>
      </button>
      <button class="btn btn--block" data-action="contrast" type="button">
        <svg class="icon"><use href="#i-divide"/></svg>
        <span>対立軸の書籍を探す</span>
      </button>
      <button class="btn btn--block" data-action="quote" type="button">
        <svg class="icon"><use href="#i-quote"/></svg>
        <span>引用カードを作る</span>
      </button>
      <button class="btn btn--block" data-action="my-bib" type="button">
        <svg class="icon"><use href="#i-book"/></svg>
        <span>マイ書誌として書き出す</span>
      </button>
      ${coverSrc ? `<button class="btn btn--block btn--ghost" data-action="open-illustrate" type="button">
        <svg class="icon"><use href="#i-sparkles"/></svg>
        <span>AI で書影イラストを再生成</span>
      </button>` : ''}
    </div>

    <p class="section-label">edit</p>
    <div class="book-detail__status">
      <p style="margin:0 0 8px;font-size:11px;color:var(--mute);font-family:var(--font-mono);letter-spacing:0.12em;">status</p>
      <div class="status-toggle" role="group">
        ${renderStatusBtn('積読', book.status === '積読')}
        ${renderStatusBtn('読書中', book.status === '読書中')}
        ${renderStatusBtn('読了', book.status === '読了')}
      </div>
      ${book.localOnly ? '' : '<p style="margin:8px 0 0;font-size:11px;color:var(--mute);line-height:1.55;">Notion 同期書籍のステータス変更はローカル限定です。Notion 側の値は次回同期で書き戻されます。</p>'}
    </div>

    <details class="ritual-details">
      <summary>読書儀式を編集する</summary>
      <div class="form-group" style="margin-top:14px;">
        <label class="form-label">この本との出会い</label>
        <input class="form-input" id="r-acq" type="text" value="${escapeHtml(book.acquisition || '')}" placeholder="どこでこの本を知った？" />
      </div>
      <div class="form-group">
        <label class="form-label">読書前の問い</label>
        <textarea class="form-textarea" id="r-pre-q" rows="2" placeholder="この本に何を問いたいですか？">${escapeHtml(book.preReadingQuestion || '')}</textarea>
        ${book.preReadingQuestion && book.status === '読了' && (book.note || '').length > 100 ? `
          <button class="btn btn--small btn--ghost" id="btn-reflect" type="button" style="margin-top:6px;">
            <svg class="icon"><use href="#i-sparkles"/></svg>
            <span>最初の問いとメモの距離を AI に振り返ってもらう</span>
          </button>
          <div id="reflect-result" hidden></div>
        ` : ''}
      </div>
      <div class="form-group">
        <label class="form-label">本を閉じる時の一言</label>
        <textarea class="form-textarea" id="r-closing" rows="2" placeholder="この本を閉じた瞬間に、一言だけ書き留める">${escapeHtml(book.closingNote || '')}</textarea>
      </div>
      <button class="btn btn--ink btn--small btn--block" id="btn-save-ritual" type="button">
        <svg class="icon"><use href="#i-check"/></svg>
        <span>儀式を保存</span>
      </button>
    </details>
    ${book.localOnly ? `
      <button class="btn btn--block btn--ghost" data-action="delete-book" type="button" style="margin-top:14px;color:var(--mute);">
        <svg class="icon"><use href="#i-trash"/></svg>
        <span>この書籍を削除</span>
      </button>
    ` : `
      <p style="margin:14px 0 0;font-size:11px;color:var(--mute);line-height:1.55;text-align:center;">Notion 同期書籍はアプリから削除できません。<br/>削除したい場合は Notion 側で行ってください。</p>
    `}
    <p class="section-label">find this book</p>
    <div class="book-detail__links">
      <a class="btn btn--small" href="${escapeHtml(amazonUrl)}" target="_blank" rel="noopener noreferrer">
        <span>Amazon</span>
        <svg class="icon"><use href="#i-link"/></svg>
      </a>
      <a class="btn btn--small" href="${escapeHtml(koshoUrl)}" target="_blank" rel="noopener noreferrer">
        <span>日本の古本屋</span>
        <svg class="icon"><use href="#i-link"/></svg>
      </a>
      ${notionUrl ? `<a class="btn btn--small" href="${escapeHtml(notionUrl)}" target="_blank" rel="noopener noreferrer"><span>Notion</span><svg class="icon"><use href="#i-link"/></svg></a>` : ''}
      ${extractBooxPdfUrl(book.note) ? `<a class="btn btn--small" href="${escapeHtml(extractBooxPdfUrl(book.note))}" target="_blank" rel="noopener noreferrer"><span>BOOX 手書きメモ</span><svg class="icon"><use href="#i-link"/></svg></a>` : ''}
    </div>
  `;

  const screen = showScreen('book-detail', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('[data-action="analyze"]').addEventListener('click', () => showAnalyzeScreen(book));
  screen.querySelector('[data-action="contrast"]').addEventListener('click', () => showContrastScreen(book));
  screen.querySelector('[data-action="quote"]').addEventListener('click', () => showQuoteCardScreen(book));
  screen.querySelector('[data-action="book-chat"]').addEventListener('click', async () => {
    window.history.pushState({ screen: 'book-chat' }, '', `#book-chat/${book.id}`);
    const { showBookChatScreen } = await import('./book-chat.js');
    showBookChatScreen(book);
  });
  screen.querySelector('[data-action="my-bib"]').addEventListener('click', async () => {
    window.history.pushState({ screen: 'my-bibliography' }, '', `#my-bib/${book.id}`);
    const { showMyBibliographyScreen } = await import('./my-bibliography.js');
    showMyBibliographyScreen(book);
  });
  const openIllust = screen.querySelector('[data-action="open-illustrate"]');
  if (openIllust) openIllust.addEventListener('click', () => openIllustrateModal(book));
  bindNextQuestion(screen, book);
  bindStatusToggle(screen, book);
  bindDeleteAction(screen, book);
  bindNoteAppend(screen, book);
  bindRitualEdit(screen, book);
  bindHighlights(screen, book);
  paintEngagement(book);
}

async function paintEngagement(book) {
  try {
    const { score } = await computeEngagement(book);
    const el = document.getElementById('engagement-stars');
    if (!el) return;
    const filled = '★'.repeat(score);
    const empty = '☆'.repeat(5 - score);
    el.innerHTML = `<span style="font-size:18px;letter-spacing:0.1em;color:var(--ink);">${filled}<span style="color:var(--mute);">${empty}</span></span>`;
  } catch (e) {
    console.error(e);
  }
}

function bindRitualEdit(screen, book) {
  const saveBtn = screen.querySelector('#btn-save-ritual');
  if (!saveBtn) return;
  saveBtn.addEventListener('click', async () => {
    const acquisition = screen.querySelector('#r-acq').value.trim();
    const preReadingQuestion = screen.querySelector('#r-pre-q').value.trim();
    const closingNote = screen.querySelector('#r-closing').value.trim();
    const updated = { ...book, acquisition, preReadingQuestion, closingNote, updatedAt: new Date().toISOString() };
    await upsertBook(updated);
    toast('儀式を保存しました');
    showBookDetailScreen(book.id);
  });

  const reflectBtn = screen.querySelector('#btn-reflect');
  if (reflectBtn) {
    reflectBtn.addEventListener('click', async () => {
      showLoading('AI が振り返り中…');
      try {
        const result = await fetchPreReadingReflection(
          { name: book.name, author: book.author, note: book.note },
          book.preReadingQuestion
        );
        const div = screen.querySelector('#reflect-result');
        div.hidden = false;
        div.innerHTML = `
          <div style="margin-top:10px;padding:12px 14px;border:1px solid var(--line);background:var(--paper);">
            <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">最初の問いへの応答</p>
            <p style="margin:0 0 10px;font-family:var(--font-serif);font-size:13.5px;line-height:1.85;">${escapeHtml(result.answered || '')}</p>
            <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">逸脱の方向</p>
            <p style="margin:0 0 10px;font-family:var(--font-serif);font-size:13.5px;line-height:1.85;color:var(--dim);">${escapeHtml(result.drift || '')}</p>
            <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">いま立てたい新しい問い</p>
            <p style="margin:0;font-family:var(--font-serif);font-size:14px;line-height:1.85;">${escapeHtml(result.new_question || '')}</p>
          </div>
        `;
        await saveAiHistory({
          type: 'pre-reading-reflection',
          title: `${book.name} の問い振り返り`,
          context: { bookId: book.id },
          payload: result,
        });
      } catch (e) {
        console.error(e);
        toast('振り返りに失敗しました');
      } finally {
        hideLoading();
      }
    });
  }
}

async function bindHighlights(screen, book) {
  const box = screen.querySelector('#highlights-box');
  if (!box) return;
  await paintHighlights(book.id, box);

  // 書籍メモのテキスト選択時に「ハイライト」ボタンを浮かす
  const noteEl = screen.querySelector('.book-detail__note');
  if (!noteEl) return;
  let popup = null;
  const removePopup = () => {
    if (popup) { popup.remove(); popup = null; }
  };
  const onSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) { removePopup(); return; }
    const text = sel.toString().trim();
    if (text.length < 4 || text.length > 500) { removePopup(); return; }
    if (!noteEl.contains(sel.anchorNode)) { removePopup(); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    removePopup();
    popup = document.createElement('button');
    popup.type = 'button';
    popup.className = 'hl-popup';
    popup.textContent = '+ ハイライト';
    popup.style.position = 'fixed';
    popup.style.top = `${rect.top - 36 + window.scrollY * 0}px`;
    popup.style.left = `${rect.left + rect.width / 2 - 60}px`;
    popup.addEventListener('click', async () => {
      await addHighlight(book.id, text);
      sel.removeAllRanges();
      removePopup();
      toast('ハイライトしました');
      await paintHighlights(book.id, box);
    });
    document.body.appendChild(popup);
  };
  document.addEventListener('selectionchange', onSelection);
  noteEl.addEventListener('mousedown', removePopup);
}

async function paintHighlights(bookId, container) {
  const highlights = await getHighlightsByBook(bookId);
  if (highlights.length === 0) {
    container.innerHTML = '';
    return;
  }
  container.innerHTML = `
    <p class="section-label">my highlights · ${highlights.length}</p>
    <ul class="hl-list">
      ${highlights.map((h) => `
        <li class="hl-item">
          <p class="hl-item__text">${escapeHtml(h.text)}</p>
          <button class="btn btn--icon hl-item__del" data-hl-del="${h.id}" type="button" aria-label="削除">
            <svg class="icon" style="width:12px;height:12px;color:var(--mute);"><use href="#i-close"/></svg>
          </button>
        </li>
      `).join('')}
    </ul>
  `;
  container.querySelectorAll('[data-hl-del]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = parseInt(btn.dataset.hlDel, 10);
      await deleteHighlight(id);
      await paintHighlights(bookId, container);
    });
  });
}

function bindNoteAppend(screen, book) {
  const btn = screen.querySelector('#btn-append-note');
  const ta = screen.querySelector('#note-append-text');
  if (!btn || !ta) return;
  btn.addEventListener('click', async () => {
    const text = ta.value.trim();
    if (!text) {
      toast('メモを入力してください');
      return;
    }
    btn.disabled = true;
    try {
      if (!book.localOnly) {
        // Notion に書き戻し（pageId === book.id）
        await appendNotionNote(book.id, text);
      }
      // ローカル note も即時更新（次回同期で Notion から再 fetch されるが、即時反映で UX 良い）
      const newNote = (book.note ? book.note + '\n\n' : '') + text;
      const updated = { ...book, note: newNote, updatedAt: new Date().toISOString() };
      await upsertBook(updated);
      toast(book.localOnly ? 'メモを保存しました' : 'Notion に追記しました');
      // 詳細を再描画
      showBookDetailScreen(book.id);
    } catch (e) {
      console.error(e);
      toast('追記に失敗しました');
    } finally {
      btn.disabled = false;
    }
  });
}

function renderStatusBtn(label, active) {
  const cls = active ? 'btn btn--ink btn--small' : 'btn btn--small';
  return `<button type="button" class="${cls}" data-set-status="${label}">${label}</button>`;
}

function bindStatusToggle(screen, book) {
  screen.querySelectorAll('[data-set-status]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const next = btn.dataset.setStatus;
      if (next === book.status) return;
      const updated = {
        ...book,
        status: next,
        startDate: next === '読書中' && !book.startDate ? new Date().toISOString().slice(0, 10) : book.startDate,
        endDate: next === '読了' && !book.endDate ? new Date().toISOString().slice(0, 10) : book.endDate,
        updatedAt: new Date().toISOString(),
      };
      await upsertBook(updated);
      toast(`ステータスを「${next}」に変更しました`);
      // 詳細を再描画して、active のトグルを反映
      showBookDetailScreen(book.id);
    });
  });
}

function bindDeleteAction(screen, book) {
  const btn = screen.querySelector('[data-action="delete-book"]');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const msg = book.localOnly
      ? 'この書籍を完全に削除します。よろしいですか？'
      : 'ローカルキャッシュからこの書籍を削除します。次回の Notion 同期で再取得される可能性があります。よろしいですか？';
    if (!confirm(msg)) return;
    await deleteBook(book.id);
    toast('削除しました');
    backToHome();
  });
}

// 「次の問い」ボタンと結果領域のバインド。同じ書籍の履歴があれば最新版を即時表示する。
async function bindNextQuestion(screen, book) {
  const btn = screen.querySelector('#btn-next-question');
  if (!btn) return;
  const result = screen.querySelector('#next-question-result');

  // 過去の履歴があれば最新を再表示
  try {
    const history = await getAiHistoryByType('next-question');
    const last = history.find((h) => h.context?.bookId === book.id);
    if (last?.payload?.questions?.length) {
      renderQuestions(result, last.payload.questions, last.createdAt);
    }
  } catch {}

  btn.addEventListener('click', async () => {
    showLoading('AI が次の問いを生成中…');
    try {
      const res = await fetchNextQuestion({
        name: book.name,
        author: book.author,
        genre: book.genre,
        note: book.note,
      });
      renderQuestions(result, res.questions || [], Date.now());
      await saveAiHistory({
        type: 'next-question',
        title: book.name,
        context: { bookId: book.id },
        payload: res,
      });
    } catch (e) {
      console.error(e);
      toast('生成に失敗しました');
    } finally {
      hideLoading();
    }
  });
}

function renderQuestions(container, questions, ts) {
  if (!questions || !questions.length) return;
  const dateStr = ts
    ? new Date(ts).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
    : '';
  container.hidden = false;
  container.innerHTML = `
    <div style="margin-top:14px;padding:14px 16px;border:1px solid var(--line);background:var(--paper);">
      <p style="margin:0 0 8px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">next questions${dateStr ? ' · ' + escapeHtml(dateStr) : ''}</p>
      <ol style="margin:0;padding-left:20px;font-family:var(--font-serif);font-size:14px;line-height:1.8;color:var(--ink);">
        ${questions.map((q) => `<li style="margin-bottom:6px;">${escapeHtml(q)}</li>`).join('')}
      </ol>
    </div>
  `;
}

function openIllustrateModal(book) {
  const themeSeed = (book.genre || []).join(' / ');
  const html = `
    ${screenHead('AI で書影を生成')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      この書籍の「学べる主題・雰囲気」を入力してください。AI が hand-drawn editorial スタイルで書影イラストを生成します（既存 17 冊と同じトーン）。
    </p>
    <div style="margin-bottom:16px;padding:12px 16px;border:1px solid var(--line);">
      <p style="margin:0 0 2px;font-family:var(--font-serif);font-size:15px;font-weight:500;">${escapeHtml(book.name)}</p>
      <p style="margin:0;font-size:12px;color:var(--mute);">${escapeHtml(book.author || '')}</p>
    </div>
    <div class="form-group">
      <label class="form-label">学べる主題・雰囲気</label>
      <textarea class="form-textarea" id="theme-input" rows="3" placeholder="例: 写真の存在論・プンクトゥム / 構造主義人類学・神話的思考">${escapeHtml(themeSeed)}</textarea>
    </div>
    <div class="form-group">
      <label class="form-label">装丁画像（任意・自分の参考用、AI には送られません）</label>
      <input class="form-input" id="cover-upload" type="file" accept="image/*" />
      <div id="cover-preview" style="margin-top:8px;text-align:center;"></div>
    </div>
    <button class="btn btn--ink btn--block" id="btn-generate" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>生成する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: gpt-image-2 · 1回あたり 約 $0.04
    </p>
  `;
  const screen = showScreen('illustrate-modal', html);
  // このモーダルは独立した履歴エントリを積んでいない（オーバーレイ扱い）。
  // window.history.back() を呼ぶと book-detail の履歴エントリまで pop してしまい、
  // 以降「戻る」が無反応になる。history は触らず、詳細をその場で再描画して戻る。
  screen.querySelector('[data-action="back"]').addEventListener('click', () => showBookDetailScreen(book.id));

  // ファイル選択時にプレビューだけ表示（IndexedDB には保存しない、AI にも送らない）
  const fileInput = screen.querySelector('#cover-upload');
  const preview = screen.querySelector('#cover-preview');
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    preview.innerHTML = `<img src="${url}" alt="" style="max-width:140px;border:1px solid var(--line);" />`;
  });

  screen.querySelector('#btn-generate').addEventListener('click', async () => {
    const theme = screen.querySelector('#theme-input').value.trim();
    if (!theme) {
      toast('学べる主題を入力してください');
      return;
    }
    showLoading('AI が書影イラストを生成中…');
    try {
      const blob = await requestIllustration({ title: book.name, author: book.author, theme });
      // Blob のまま IndexedDB に入れると WebKit でアプリ再起動後に失われるため、
      // data URL 文字列に変換して保存する（書影が消えて再生成＝再課金になる問題の対策）。
      const dataUrl = await blobToDataUrl(blob);
      const updated = { ...book, illustrationDataUrl: dataUrl, hasIllustration: false };
      delete updated.illustrationBlob;
      await upsertBook(updated);
      toast('書影を生成しました');
      // モーダルは履歴を積んでいないので history.back() は使わず、
      // その場で詳細を再描画する（戻るボタン無反応バグの原因を断つ）。
      showBookDetailScreen(book.id);
    } catch (e) {
      console.error(e);
      toast('生成に失敗しました');
    } finally {
      hideLoading();
    }
  });
}
